import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NavItem } from '../../config/navigationConfig.types';
import { NavTierRenderer } from './NavTierRenderer';
import { NavTier1AccordionSummary, NavTier2AccordionSummary, NavText } from './navStyles';
import { NAV_TEXT_COLOR, NAV_HOVER_BG_MAIN, NAV_HOVER_BG_SUB } from './navConstants';
import { navIconMap, defaultNavIcon } from './navIconMap';
import { NavIconImage } from './NavIconImage';
import { useApplicationContext } from '../../context/ApplicationContext';
import { useActiveNavItem } from '../../hooks/useActiveNavItem';

interface NavAccordionProps {
  item: NavItem;
  tier: 1 | 2 | 3;
  isNavOpen: boolean;
  isOpen: boolean;
  onToggle: (id: string, expanded: boolean) => void;
}

export const NavAccordion: React.FC<NavAccordionProps> = ({ item, tier, isNavOpen, isOpen, onToggle }) => {
  const { activeNavItemId, filteredNavigation } = useApplicationContext();
  const { getActiveItemId, hasActiveDescendant } = useActiveNavItem(filteredNavigation);

  // Get the active ID (either explicit or inferred)
  const currentActiveId = activeNavItemId ?? getActiveItemId();
  const isDescendantActive = hasActiveDescendant(item, currentActiveId);

  const toggle = (_: React.SyntheticEvent, expanded: boolean) => {
    onToggle(item.id, expanded);
  };

  const handleClick = (e: React.MouseEvent) => {
    // Only remove focus for actual mouse clicks (e.detail > 0)
    // Keyboard activations (space/enter) have e.detail === 0 and should retain focus
    if (e.detail > 0) {
      (e.currentTarget as HTMLElement).blur();
    }
  };

  const nextTier = Math.min(tier + 1, 3) as 1 | 2 | 3;

  const SummaryComponent = tier === 1 ? NavTier1AccordionSummary : NavTier2AccordionSummary;
  const expandIconSize = tier === 1 ? '24px' : '20px';

  return (
    <Accordion
      expanded={isOpen}
      onChange={toggle}
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        backgroundColor: 'transparent',
        border: 'none',
        marginBottom: tier === 1 ? 1.5 : 0,
      }}
    >
      <SummaryComponent
        id={`nav-accordion-header-${item.id}`}
        aria-controls={`nav-accordion-panel-${item.id}`}
        onClick={handleClick}
        expandIcon={
          isNavOpen ? (
            <ExpandMoreIcon sx={{ fontSize: expandIconSize, color: NAV_TEXT_COLOR }} />
          ) : null
        }
        sx={{
          backgroundColor: isDescendantActive 
            ? (tier === 1 ? NAV_HOVER_BG_MAIN : NAV_HOVER_BG_SUB) 
            : 'transparent',
        }}
      >
        {tier === 1 && item.icon && (
          <NavIconImage src={navIconMap[item.icon] ?? defaultNavIcon} />
        )}
        <NavText
          tier={tier}
          sx={{
            opacity: isNavOpen ? 1 : 0,
            transition: 'opacity 200ms',
            fontWeight: isDescendantActive ? 600 : 400,
          }}
        >
          {item.label}
        </NavText>
      </SummaryComponent>
      <AccordionDetails
        id={`nav-accordion-panel-${item.id}`}
        aria-labelledby={`nav-accordion-header-${item.id}`}
        sx={{
          py: 0,
          px: 0,
          display: isNavOpen ? 'block' : 'none',
        }}
      >
        <NavTierRenderer items={item.children!} tier={nextTier} isNavOpen={isNavOpen} />
      </AccordionDetails>
    </Accordion>
  );
};
